import { useState, forwardRef, useEffect } from 'react';
import { FAIcon } from '../Icons/Icons';
import { Breakpoints, useBreakpoint } from '../Breakpoint/BreakpointProvider';
import { LinkTarget, Theme } from '../../types/enums';
import { TextLinkUnderlineStyle } from '../TextLink/TextLink';
import RawTextLink from '../TextLink/RawTextLink';
import { NotificationBarPropType, NotificationBarTheme } from './types/propTypes';
import { NOTIFICATION_BAR_MOBILE_TEXT_LIMIT } from './types/enums';
import styles from './NotificationBar.module.scss';

/**
 * @description
 * Notification Bar component used to display full with bar with blue color theme.
 * Figma:- https://www.figma.com/file/NmGzJoDRtkn481G3lgg0Z9/Amway-DS-–%C2%A0Global-Core-Components?t=rf95r7TuC5gW3g13-0
 * @param message
 * @type string
 * @description to display in notification bar.
 *
 * @param icon
 * @type iconName
 * @description to display custom icon.
 *
 *
 * @param iconStyle
 * @type IconStyle
 * @description to display custom icon's style i.e., solid, regular, brands.
 *
 * @param linkText
 * @type string
 * @description text to display link.
 *
 * @param linkRedirect
 * @type string
 * @description redirection link on click of link text.
 *
 * @param linkTarget
 * @type string
 * @description open the link in same window or others.
 *
 * @param iconClickHandler
 * @type function
 * @description click handler for icon.
 * @returns Notification Bar React Node
 *  *
 * @param ctaLabel
 * @type string
 * @description take label to show for action.
 *
 * @param theme
 * @type string
 * @description background color
 *
 * @param ctaClickHandler
 * @type function
 * @description handle click event
 *
 * @param isCloseIconRequired
 * @type boolean
 * @description close button required
 */
const NotificationBar = forwardRef<HTMLDivElement, NotificationBarPropType>(
  ({ message, theme = Theme.PURPLE, ctaLabel, ctaClickHandler, iconClickHandler }: NotificationBarPropType, ref) => {
    const [currentMessage, setCurrentMessage] = useState<string>();
    const viewPortType = useBreakpoint();
    /**
     * @function processMessage updates message text length as per responsiveness.
     * specific to mobile view
     */
    function processMessage() {
      let wordLimit: number = message.length;
      if (ctaLabel && iconClickHandler) {
        wordLimit = NOTIFICATION_BAR_MOBILE_TEXT_LIMIT.WITH_CLOSE_CTA;
      } else if (ctaLabel) {
        wordLimit = NOTIFICATION_BAR_MOBILE_TEXT_LIMIT.WITH_CTA_ONLY;
      } else if (iconClickHandler) {
        wordLimit = NOTIFICATION_BAR_MOBILE_TEXT_LIMIT.WITH_CLOSE_ONLY;
      } else {
        wordLimit = NOTIFICATION_BAR_MOBILE_TEXT_LIMIT.NO_CLOSE_NO_CTA;
      }
      setCurrentMessage(message.substring(0, wordLimit));
    }
    useEffect(() => {
      [Breakpoints.XS, Breakpoints.SM].includes(viewPortType as Breakpoints)
        ? processMessage()
        : setCurrentMessage(message);
    }, [message, viewPortType]);

    return (
      <section className={`${styles['notification-bar-container']} ${styles[theme]}`} ref={ref}>
        <article className={styles['notification-bar-content']}>
          <span className={styles['notification-bar-message']} data-testid="notification-message">
            {currentMessage}
          </span>
          {ctaLabel && (
            <RawTextLink
              href=""
              text={ctaLabel}
              theme={Theme.WHITE}
              onClick={ctaClickHandler}
              underline={TextLinkUnderlineStyle.FIXED}
            />
          )}
        </article>
        {iconClickHandler && (
          <FAIcon
            iconName="xmark"
            theme={Theme.WHITE}
            className={styles['icon-x']}
            onClick={iconClickHandler}
            data-testid="notification-icon"
          />
        )}
      </section>
    );
  },
);
NotificationBar.displayName = 'NotificationBar';

export default NotificationBar;
export { LinkTarget };
export type { NotificationBarPropType, NotificationBarTheme };
